import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import ExcelJS from "exceljs";
import Papa from "papaparse";

declare global {
  interface Window {
    AdobeDC: any;
  }
}

interface DocumentViewerProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileType: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileType,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [adobeError, setAdobeError] = useState(false);

  const normalizedType = (fileType || "").toLowerCase();
  const lowerName = (fileName || "").toLowerCase();
  const adobeClientid = import.meta.env.VITE_ADOBE_CLIENT_ID;


  const isPdf =
    normalizedType.includes("pdf") ||
    lowerName.endsWith(".pdf");

  const isDoc =
    normalizedType.includes("doc") ||
    lowerName.endsWith(".doc") ||
    lowerName.endsWith(".docx");

  const isTxt =
    normalizedType.includes("txt") ||
    normalizedType.includes("text") ||
    normalizedType === "plain" ||
    normalizedType.includes("text/plain") ||
    lowerName.endsWith(".txt");

  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp"]
    .some(ext => normalizedType.includes(ext) || lowerName.endsWith(`.${ext}`));

  const isExcel =
    normalizedType.includes("excel") ||
    normalizedType.includes("spreadsheet") ||
    lowerName.endsWith(".xlsx") ||
    lowerName.endsWith(".xls");

  const isCsv =
    normalizedType.includes("csv") ||
    lowerName.endsWith(".csv");

  const CsvViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
    const [rows, setRows] = useState<string[][]>([]);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const MAX_PREVIEW_ROWS = 1000;
    const previewRows = rows.slice(0, MAX_PREVIEW_ROWS);

    useEffect(() => {
      const loadCsv = async () => {
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to load CSV (${response.status})`);
          }
          const text = await response.text();

          const result = Papa.parse<string[]>(text, {
            skipEmptyLines: true,
          });

          if (result.errors.length) {
            console.error(result.errors);
            setError(true);
            return;
          }

          setRows(result.data);
        } catch (err) {
          console.error("CSV preview error", err);
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      loadCsv();
    }, [fileUrl]);

    if (error) {
      return (
        <Alert severity="error">
          Failed to load CSV preview
        </Alert>
      );
    }

    if (loading) {
      return <CircularProgress />;
    }

    if (!rows?.length) {
        return (
            <Alert severity="info">
                No data found.
            </Alert>
        );
    }


    return (
      <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
          }}
        >
          <tbody>
            {previewRows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      fontSize: 14,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const ExcelViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
    const [rows, setRows] = useState<any[][]>([]);
    const [error, setError] = useState(false);

    useEffect(() => {
      const loadExcel = async () => {
        try {
          const res = await fetch(fileUrl);
          const buffer = await res.arrayBuffer();

          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);

          const worksheet = workbook.worksheets?.[0];
          if (!worksheet) {
            setError(true);
            return;
          }

          const data: any[][] = [];   // ✅ MUST be declared here

          worksheet.eachRow((row) => {
            const values = row.values as any[];
            data.push(values.slice(1)); // remove empty first index
          });

          setRows(data);
        } catch (e) {
          console.error("Excel preview error", e);
          setError(true);
        }
      };

      loadExcel();
    }, [fileUrl]);

    if (error) {
      return <Alert severity="error">Failed to load Excel preview</Alert>;
    }

    return (
      <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      fontSize: 14,
                    }}
                  >
                    {cell ? String(cell) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const TxtViewer: React.FC<{ fileUrl: string; fileName: string }> = ({ fileUrl }) => {
    const [content, setContent] = useState<string>("Loading...");
    const [error, setError] = useState(false);

    useEffect(() => {
      let active = true;

      const loadText = async () => {
        try {
          const response = await fetch(fileUrl);
          const text = await response.text();
          if (active) setContent(text);
        } catch (err) {
          console.error("TXT preview error", err);
          setError(true);
        }
      };

      loadText();
      return () => { active = false };
    }, [fileUrl]);

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load text preview
        </Alert>
      );
    }

    return (
      <Box
        sx={{
          p: 3,
          height: "100%",
          overflow: "auto",
          backgroundColor: "#fafafa",
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content}
      </Box>
    );
  };
  useEffect(() => {
    setAdobeError(false);
  }, [fileUrl]);

  useEffect(() => {
    if (!open || !isPdf || !fileUrl) return;
    const initViewer = async () => {
      try {
        if (!window.AdobeDC) return;

        await new Promise(resolve => setTimeout(resolve, 0));

        if (!containerRef.current) return;

        containerRef.current.innerHTML = "";

        const view = new window.AdobeDC.View({
          clientId: adobeClientid,
          divId: `adobe-pdf-view-${fileName}`,
        });

        if (fileUrl.startsWith("blob:")) {
          const response = await fetch(fileUrl);
          const arrayBuffer = await response.arrayBuffer();

          await view.previewFile(
            {
              content: { promise: Promise.resolve(arrayBuffer) },
              metaData: { fileName },
            },
            { embedMode: "SIZED_CONTAINER" }
          );
          return;
        }

        if (fileUrl.startsWith("http")) {
          await view.previewFile({
            content: { location: { url: fileUrl } },
            metaData: { fileName },
          });
        }

      } catch (err) {
        console.error("Adobe preview error", err);
        setAdobeError(true);
      }
    };

    const scriptId = "adobe-pdf-sdk";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://documentcloud.adobe.com/view-sdk/main.js";
      script.onload = initViewer;
      document.body.appendChild(script);
    } else {
      initViewer();
    }
  }, [open, isPdf, fileUrl, fileName, adobeClientid]);

  const renderViewer = () => {
    if (isPdf) {
      if (adobeError) {
        return (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load preview
          </Alert>
        );
      }

      return (
        <Box sx={{ height: "100%", minHeight: 500 }}>
          <div ref={containerRef} id={`adobe-pdf-view-${fileName}`} style={{ height: "100%" }} />
        </Box>
      );
    }

    if (isCsv) {
      return <CsvViewer fileUrl={fileUrl} />;
    }

    if (isExcel) {
      return <ExcelViewer fileUrl={fileUrl} />;
    }

    if (isDoc) {
      const docType = lowerName.endsWith(".docx") ? "docx" : "doc";

      return (
        <Box sx={{ height: "100%", minHeight: 600 }}>
          <DocViewer
            documents={[
              {
                uri: fileUrl,
                fileType: docType,
              },
            ]}
            pluginRenderers={DocViewerRenderers}
            config={{
              header: { disableHeader: true },
            }}
            style={{ height: "100%" }}
          />
        </Box>
      );
    }

    if (isTxt) {
      return <TxtViewer fileUrl={fileUrl} fileName={fileName} />;
    }

    if (isImage) {
      return (
        <Box sx={{ height: "100%", minHeight: 500, alignItems: "center", display: "flex", justifyContent: "center" }}>
          <img
            src={fileUrl}
            alt={fileName}
            style={{ maxWidth: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Preview not available. Please download the file.
        </Alert>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "90%",
          maxWidth: "1300px",
          height: "100vh",
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h3">{fileName}</Typography>
        <IconButton onClick={onClose} sx={{ backgroundColor:"#F2F2F4", ":hover": { backgroundColor: "#F2F2F4" } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>{renderViewer()}</DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;